import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent, MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyChipsModule } from '@angular/material/legacy-chips';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { Store } from '@ngxs/store';
import { DirectivesModule } from '@shared/directives/directives.module';
import { TaskActions } from '@tasks/state/actions';
import { Task } from '@tasks/tasks.types';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { Contact } from 'app/modules/contacts/contacts.types';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith } from 'rxjs/operators';
import { ContactFactory } from 'tests/factories/contact.factory';

@Component({
  selector: 'app-task-details-contacts',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatLegacyAutocompleteModule,
    MatLegacyChipsModule,
    MatLegacyFormFieldModule,
    MatIconModule,
    DirectivesModule,
  ],
  template: `
    <div class="">
      <div class=" ">
        <mat-form-field class="w-full field treo-mat-no-subscript py-3">
          <mat-label>Contacts</mat-label>
          <mat-chip-list #chipList aria-label="User selection" class="mat-chip-list my-2">
            <mat-chip
              *ngFor="let contact of task.contacts"
              [selectable]="selectable"
              [removable]="removable"
              (removed)="removeContact(contact)"
              [appAuthorize]="canEditTask"
              denyAction="disable"
            >
              {{ contact.name }}
              <mat-icon matChipRemove *ngIf="removable" class="m-0">cancel</mat-icon>
            </mat-chip>
            <input
              placeholder="Add Contacts..."
              #userInput
              [formControl]="userCtrl"
              [matAutocomplete]="auto"
              [matChipInputFor]="chipList"
              [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
              [appAuthorize]="canEditTask"
              denyAction="disable"
              [attr.data-testid]="'task-details-contacts-input'"
              class="p-2"
            />
          </mat-chip-list>
          <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selectContact($event)">
            <mat-option
              *ngFor="let user of contactList"
              [value]="user.name"
              [id]="user.id"
              [appAuthorize]="canEditTask"
              denyAction="disable"
            >
              {{ user.name }}
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
      </div>
    </div>
  `,
  styleUrl: './task-details-contacts.component.scss',
})
export class TaskDetailsContactsComponent implements OnInit {
  @Input() task: Task;
  @Input() canEditTask: boolean;
  @Output() contactAdd = new EventEmitter<Partial<Contact>>();
  @Output() contactRemove = new EventEmitter<Contact>();

  private destroyRef = inject(DestroyRef);

  contactsService = inject(ContactsService);
  selectable = true;
  removable = true;
  separatorKeysCodes: number[] = [ENTER];
  contactList;

  userCtrl: UntypedFormControl;

  @ViewChild('userInput')
  userInput: ElementRef<HTMLInputElement>;

  @ViewChild('auto')
  matAutocomplete: MatAutocomplete;

  constructor(private store: Store) {
    this.userCtrl = new FormControl();
  }

  ngOnInit(): void {
    this.userCtrl.valueChanges
      .pipe(debounceTime(400), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((query: string) => {
        this.contactsService
          .getContacts(1, 20, 'first_name', 'asc', query)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((response: any) => {
            this.contactList = response.contacts;
          });
      });
  }

  removeContact(contact: Contact): void {
    this.contactRemove.emit(contact);
  }

  selectContact(event: MatAutocompleteSelectedEvent): void {
    const newContact = {
      id: event.option.id,
      name: event.option.viewValue,
    };

    this.userInput.nativeElement.value = '';
    this.userCtrl.setValue(null);

    this.contactAdd.emit(newContact);
  }
}
